import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ArrowLeft,
  X,
  Loader2,
  TrendingUp,
  Clock,
  Star,
  Mic,
  Flame,
  Leaf,
  ChefHat,
  Heart,
  Gift,
  Award,
  Globe,
  Utensils,
  Coffee,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useShopifySearch } from '../hooks/useShopifySearch';
import { useCollectionProducts } from '../hooks/useShopifyProducts';
import type { Product } from './ProductCard';

interface MobileSearchPopupProps {
  isVisible: boolean;
  onClose: () => void;
  onProductClick: (product: Product) => void;
  onViewAllResults: (query: string) => void;
  onAddToCart: (product: Product) => void;
  initialQuery?: string;
}

// Recent searches - in a real app, this would be stored in localStorage
const getRecentSearches = (): string[] => {
  try {
    const stored = localStorage.getItem('olispices_recent_searches');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveRecentSearch = (query: string) => {
  try {
    const recent = getRecentSearches();
    const updated = [query, ...recent.filter(q => q !== query)].slice(0, 5);
    localStorage.setItem('olispices_recent_searches', JSON.stringify(updated));
  } catch {
    // Ignore localStorage errors
  }
};

const trendingSearches = [
  'Christmas Spices',
  'Garam Masala',
  'BBQ Rub',
  'Saffron',
  'Organic Turmeric',
  'Mediterranean Herbs',
];

// Function to get translated category names from MegaMenu translations
const getCategoryName = (t: any, searchTerm: string): string => {
  const categoryMap: Record<string, string> = {
    'spice-blends': t('navigation:categories.spiceBlends.name'),
    'organic': t('navigation:categories.organic.name'),
    'grill-bbq': t('navigation:categories.grillBBQ.name'),
    'paprika': t('navigation:categories.paprika.name'),
    'spices-herbs': t('navigation:categories.spicesHerbsByOrigin.name'),
    'baking': t('navigation:categories.baking.name'),
    'kitchen-essentials': t('navigation:categories.kitchenEssentials.name'),
    'hot-peppers-chili': t('navigation:categories.hotPeppersChili.name'),
    'gift-sets': t('navigation:categories.giftSets.name'),
    'flavored-salts': t('navigation:categories.flavoredSalts.name'),
    'world-cuisines': t('navigation:categories.worldCuisines.name'),
    'dish-type': t('navigation:categories.dishType.name'),
  };
  
  return categoryMap[searchTerm] || searchTerm;
};

// All available categories with their display info
const getAllCategories = (t: any) => [
  { name: getCategoryName(t, 'spice-blends'), icon: Utensils, searchTerm: 'spice-blends' },
  { name: getCategoryName(t, 'organic'), icon: Leaf, searchTerm: 'organic' },
  { name: getCategoryName(t, 'grill-bbq'), icon: Flame, searchTerm: 'grill-bbq' },
  { name: getCategoryName(t, 'paprika'), icon: Heart, searchTerm: 'paprika' },
  { name: getCategoryName(t, 'spices-herbs'), icon: Leaf, searchTerm: 'spices-herbs' },
  { name: getCategoryName(t, 'baking'), icon: Coffee, searchTerm: 'baking' },
  {
    name: getCategoryName(t, 'kitchen-essentials'),
    icon: ChefHat,
    searchTerm: 'kitchen-essentials',
  },
  { name: getCategoryName(t, 'hot-peppers-chili'), icon: Flame, searchTerm: 'hot-peppers-chili' },
  { name: getCategoryName(t, 'gift-sets'), icon: Gift, searchTerm: 'gift-sets' },
  { name: getCategoryName(t, 'flavored-salts'), icon: Award, searchTerm: 'flavored-salts' },
  { name: getCategoryName(t, 'world-cuisines'), icon: Globe, searchTerm: 'world-cuisines' },
  { name: getCategoryName(t, 'dish-type'), icon: Utensils, searchTerm: 'dish-type' },
];

// Hook to get real product counts for categories using existing Shopify hook
const useCategoryProductCounts = () => {
  const [counts, setCounts] = useState<Record<string, number>>({});

  // Create hooks for each category
  const spiceBlendsData = useCollectionProducts('spice-blends');
  const organicData = useCollectionProducts('organic');
  const grillBbqData = useCollectionProducts('for-grill-bbq');
  const paprikaData = useCollectionProducts('paprika-1');
  const spicesHerbsData = useCollectionProducts('spices-herbs');
  const bakingData = useCollectionProducts('for-baking');
  const kitchenEssentialsData = useCollectionProducts('kitchen-essentials');
  const hotPeppersData = useCollectionProducts('hot-peppers-chili');
  const giftSetsData = useCollectionProducts('gift-sets');
  const flavoredSaltsData = useCollectionProducts('flavored-salts');
  const worldCuisinesData = useCollectionProducts('by-world-cuisines');
  const dishTypeData = useCollectionProducts('by-dish-type');

  useEffect(() => {
    const newCounts: Record<string, number> = {};

    // Map data to counts
    newCounts['spice-blends'] = spiceBlendsData.products?.length || 0;
    newCounts['organic'] = organicData.products?.length || 0;
    newCounts['grill-bbq'] = grillBbqData.products?.length || 0;
    newCounts['paprika'] = paprikaData.products?.length || 0;
    newCounts['spices-herbs'] = spicesHerbsData.products?.length || 0;
    newCounts['baking'] = bakingData.products?.length || 0;
    newCounts['kitchen-essentials'] =
      kitchenEssentialsData.products?.length || 0;
    newCounts['hot-peppers-chili'] = hotPeppersData.products?.length || 0;
    newCounts['gift-sets'] = giftSetsData.products?.length || 0;
    newCounts['flavored-salts'] = flavoredSaltsData.products?.length || 0;
    newCounts['world-cuisines'] = worldCuisinesData.products?.length || 0;
    newCounts['dish-type'] = dishTypeData.products?.length || 0;

    setCounts(newCounts);
  }, [
    spiceBlendsData.products,
    organicData.products,
    grillBbqData.products,
    paprikaData.products,
    spicesHerbsData.products,
    bakingData.products,
    kitchenEssentialsData.products,
    hotPeppersData.products,
    giftSetsData.products,
    flavoredSaltsData.products,
    worldCuisinesData.products,
    dishTypeData.products,
  ]);

  return counts;
};

// Function to get 6 random categories that change once per day
const getRandomCategories = (productCounts: Record<string, number> = {}, t?: any) => {
  // Use current date as seed for consistent randomization within 24h
  const today = new Date().toDateString(); // "Mon Jan 01 2024" format
  const seed = today.split('').reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);

  // Seeded random function
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  const allCategories = getAllCategories(t);
  
  // Shuffle array using seeded randomization
  const shuffled = [...allCategories].sort((a, b) => {
    const randomA = seededRandom(seed + allCategories.indexOf(a));
    const randomB = seededRandom(seed + allCategories.indexOf(b));
    return randomA - randomB;
  });

  return shuffled.slice(0, 6).map(cat => ({
    name: cat.name,
    icon: cat.icon,
    count: t ? t('search:mobile.categories.items', { count: productCounts[cat.searchTerm] || 0 }) : `${productCounts[cat.searchTerm] || 0} items`,
    searchTerm: cat.searchTerm,
  }));
};

export function MobileSearchPopup({
  isVisible,
  onClose,
  onProductClick,
  onViewAllResults,
  onAddToCart,
  initialQuery = '',
}: MobileSearchPopupProps) {
  const { t } = useTranslation(['search', 'navigation']);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [showSuggestions, setShowSuggestions] = useState(!initialQuery);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [popupHeight, setPopupHeight] = useState(590);
  const { search, searchResults } = useShopifySearch();
  const productCounts = useCategoryProductCounts();
  const [quickCategories, setQuickCategories] = useState(() =>
    getRandomCategories(productCounts, t)
  );

  // Update categories when product counts change
  useEffect(() => {
    if (Object.keys(productCounts).length > 0) {
      setQuickCategories(getRandomCategories(productCounts, t));
    }
  }, [productCounts, t]);

  // Update categories at midnight
  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const msUntilMidnight = tomorrow.getTime() - now.getTime();

    const timer = setTimeout(() => {
      setQuickCategories(getRandomCategories(productCounts, t));

      // Set up daily interval after first midnight
      const dailyInterval = setInterval(
        () => {
          setQuickCategories(getRandomCategories(productCounts, t));
        },
        24 * 60 * 60 * 1000
      ); // 24 hours

      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);

    return () => clearTimeout(timer);
  }, [productCounts]);

  // Load recent searches when popup opens (autofocus disabled for mobile UX)
  useEffect(() => {
    if (isVisible) {
      setRecentSearches(getRecentSearches());
      // Categories update automatically once per day, no need to refresh here
      // Note: Autofocus disabled to prevent mobile keyboard from popping up automatically
    }
  }, [isVisible]);

  // Search with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setShowSuggestions(true);
      return;
    }

    setShowSuggestions(false);

    const timer = setTimeout(() => {
      search(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, search]);

  // Handle search submission
  const handleSearch = () => {
    if (searchQuery.trim()) {
      saveRecentSearch(searchQuery);
      onViewAllResults(searchQuery);
      onClose();
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    saveRecentSearch(suggestion);
    // Trigger search after a short delay
    setTimeout(() => {
      if (suggestion.trim()) {
        onViewAllResults(suggestion);
        onClose();
      }
    }, 100);
  };

  // Prevent background scroll when popup is open and handle ESC key
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
      // Add blur effect to main content
      const mainContent =
        document.querySelector('main') ||
        document.querySelector('#root') ||
        (document.body.children[0] as HTMLElement);
      if (mainContent && mainContent !== document.body) {
        (mainContent as HTMLElement).style.filter = 'blur(2px)';
        (mainContent as HTMLElement).style.transition = 'filter 0.3s ease-out';
      }

      // Handle ESC key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);

      return () => {
        document.body.style.overflow = 'unset';
        // Remove blur effect
        if (mainContent && mainContent !== document.body) {
          (mainContent as HTMLElement).style.filter = 'none';
        }
        document.removeEventListener('keydown', handleEscape);
      };
    } else {
      document.body.style.overflow = 'unset';
      // Remove blur effect when popup is closed
      const mainContent =
        document.querySelector('main') ||
        document.querySelector('#root') ||
        (document.body.children[0] as HTMLElement);
      if (mainContent && mainContent !== document.body) {
        (mainContent as HTMLElement).style.filter = 'none';
      }
    }

    return () => {
      document.body.style.overflow = 'unset';
      // Clean up blur effect
      const mainContent =
        document.querySelector('main') ||
        document.querySelector('#root') ||
        (document.body.children[0] as HTMLElement);
      if (mainContent && mainContent !== document.body) {
        (mainContent as HTMLElement).style.filter = 'none';
      }
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-40 lg:hidden animate-in slide-in-from-top-4 duration-300"
      style={{
        background: 'rgba(0, 0, 0, 0.8)',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
      onClick={onClose}
    >
      <div
        ref={el => {
          if (el) {
            const height = el.getBoundingClientRect().height;
            if (height !== popupHeight) {
              setPopupHeight(height);
            }
          }
        }}
        className="relative flex flex-col min-h-[590px] max-h-[90vh] overflow-hidden"
        style={{
          borderBottomLeftRadius: '20px',
          borderBottomRightRadius: '20px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(25px)',
          WebkitBackdropFilter: 'blur(25px)',
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex-shrink-0 p-4 border-b border-gray-200/30"
          style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div className="flex items-center gap-3">
            {/* Back Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Button>

            {/* Search Input */}
            <div className="flex-1 relative">
              <div
                className="relative rounded-2xl border border-gray-300/50 shadow-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-amber-600" />
                <Input
                  id="mobile-search-input"
                  placeholder={t('search:mobile.searchPlaceholder')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  className="pl-12 pr-16 py-4 bg-transparent border-0 focus:ring-2 focus:ring-amber-500 placeholder:text-gray-500 text-gray-900 font-medium text-base rounded-2xl"
                />

                {/* Voice Search Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 rounded-lg text-amber-600 hover:bg-amber-50"
                  onClick={() => {
                    // Voice search functionality would be implemented here
                  }}
                >
                  <Mic className="h-4 w-4" />
                </Button>

                {/* Clear Button */}
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-10 top-1/2 transform -translate-y-1/2 h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <ScrollArea
          className="flex-1 overflow-auto"
          style={{
            background: 'transparent',
            borderBottomLeftRadius: '20px',
            borderBottomRightRadius: '20px',
          }}
        >
          <div className="p-4 space-y-6">
            {/* Loading State */}
            {searchResults.isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                  <span className="text-gray-600 font-medium">
                    {t('search:mobile.searching')}
                  </span>
                </div>
              </div>
            )}

            {/* Search Results */}
            {!searchResults.isLoading &&
              !showSuggestions &&
              searchResults.products.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">
                      {t('search:mobile.resultsFound', { 
                        count: searchResults.products.length, 
                        plural: searchResults.products.length !== 1 ? 's' : '' 
                      })}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        saveRecentSearch(searchQuery);
                        onViewAllResults(searchQuery);
                      }}
                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-xl"
                    >
                      {t('search:mobile.viewAllResults', { count: searchResults.products.length })}
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {searchResults.products.slice(0, 4).map(product => (
                      <div
                        key={product.id}
                        className="p-4 rounded-2xl border border-gray-200/30 shadow-sm transition-all duration-200 active:scale-95 hover:shadow-md"
                        style={{
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                        }}
                        onClick={() => {
                          onProductClick(product);
                          onClose();
                        }}
                      >
                        <div className="flex items-center space-x-4">
                          {/* Product Image */}
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100">
                              <ImageWithFallback
                                src={
                                  product.image || '/api/placeholder/300/300'
                                }
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>

                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-gray-900 line-clamp-1">
                                {product.name}
                              </h4>
                              {(product.tags?.includes('organic') ||
                                product.tags?.includes('premium')) && (
                                <Badge
                                  variant="secondary"
                                  className={`ml-2 text-xs ${
                                    product.tags?.includes('organic')
                                      ? 'bg-green-100 text-green-700'
                                      : product.tags?.includes('premium')
                                        ? 'bg-purple-100 text-purple-700'
                                        : 'bg-amber-100 text-amber-700'
                                  }`}
                                >
                                  {product.tags?.includes('organic')
                                    ? 'ORGANIC'
                                    : 'PREMIUM'}
                                </Badge>
                              )}
                            </div>

                            <div className="flex items-center space-x-3 mb-2">
                              <div className="flex items-center">
                                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                                <span className="text-sm text-gray-600 ml-1">
                                  {product.rating || 4.5}
                                </span>
                              </div>
                              <span className="text-sm text-gray-500">
                                {product.origin || 'Spices'}
                              </span>
                            </div>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <span className="font-semibold text-gray-900">
                                  €{product.price.toFixed(2)}
                                </span>
                              </div>

                              <Button
                                size="sm"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  onAddToCart(product);
                                }}
                                className="h-8 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
                              >
                                Add
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Empty State */}
            {!searchResults.isLoading &&
              !showSuggestions &&
              searchQuery &&
              searchResults.products.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                    <Search className="h-8 w-8 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('search:mobile.noSpicesFound')}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t('search:mobile.tryDifferentKeywords')}
                  </p>
                </div>
              )}

            {/* Suggestions */}
            {showSuggestions && (
              <div className="space-y-6">
                {/* Quick Categories */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="text-lg mr-2">🔍</span>
                    {t('search:mobile.browseCategories')}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {quickCategories.map((category, index) => (
                      <button
                        key={index}
                        onClick={() =>
                          handleSuggestionClick(category.searchTerm)
                        }
                        className="p-4 h-24 rounded-2xl border border-gray-200/30 shadow-sm text-left transition-all duration-200 active:scale-95 hover:shadow-md hover:border-amber-300 flex flex-col justify-between"
                        style={{
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <category.icon className="h-6 w-6 text-amber-600 flex-shrink-0" />
                          <span className="font-medium text-gray-900 text-sm leading-tight">
                            {category.name}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500 mt-auto">
                          {category.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <h4 className="font-medium text-gray-700">
                        {t('search:mobile.recentSearches')}
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map((search, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(search)}
                          className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200 active:scale-95"
                        >
                          {search}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trending Searches */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <TrendingUp className="h-4 w-4 text-amber-500" />
                    <h4 className="font-medium text-gray-700">
                      {t('search:mobile.trendingSearches')}
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {trendingSearches.map((search, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(search)}
                        className="px-4 py-2 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors duration-200 font-medium active:scale-95"
                      >
                        {search}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
      {/* Dimmed and blurred area below popup */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          top: `${popupHeight}px`,
          height: `calc(100vh - ${popupHeight}px)`,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(40px) saturate(200%) brightness(70%)',
          WebkitBackdropFilter: 'blur(40px) saturate(200%) brightness(70%)',
          boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)',
        }}
        onClick={onClose}
      ></div>
    </div>
  );
}
